import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import {
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format } from "date-fns";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { companyAdminCustomersAPI } from "../../lib/companyAdminApi";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function toYMD(d) {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toISOString().slice(0, 10);
}

function formatDisplayDate(raw) {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "dd MMM yyyy");
}

function fileHref(url) {
  if (!url) return "#";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function emptyUploadForm() {
  return {
    name: "",
    report_date: toYMD(new Date()),
    hba1c: "",
    file: null,
  };
}

export default function LabReportsTab({ customer, canEdit = true }) {
  const userId = customer?.user_id;
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [trend, setTrend] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState(emptyUploadForm);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", report_date: "", hba1c: "" });
  const [editSaving, setEditSaving] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await companyAdminCustomersAPI.labReportsList({
        user_id: userId,
      });
      setReports(res.data.reports || []);
      setTrend(res.data.hba1c_trend || []);
    } catch {
      setReports([]);
      setTrend([]);
      toast.error("Failed to load lab reports");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const chartData = useMemo(() => {
    return (trend || [])
      .map((p) => {
        const d = p.date ? new Date(p.date) : null;
        if (!d || Number.isNaN(d.getTime())) return null;
        const v = Number(p.hba1c);
        if (!Number.isFinite(v)) return null;
        return {
          dateLabel: format(d, "MMM yyyy"),
          dateShort: format(d, "MMM d"),
          hba1c: v,
          name: p.name || "HbA1c",
        };
      })
      .filter(Boolean);
  }, [trend]);

  const handleUpload = async () => {
    if (!form.file) return toast.error("Select a file to upload");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("user_id", userId);
      fd.append("file", form.file);
      if (form.name.trim()) fd.append("name", form.name.trim());
      if (form.report_date) fd.append("report_date", form.report_date);
      if (form.hba1c !== "" && form.hba1c != null) {
        fd.append("hba1c", String(form.hba1c));
      }
      await companyAdminCustomersAPI.labReportsUpload(fd);
      toast.success("Lab report uploaded");
      setUploadOpen(false);
      setForm(emptyUploadForm());
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.message || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const id = row?.id;
    if (!id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete report "${row.name || "this file"}"?`)) return;
    try {
      await companyAdminCustomersAPI.labReportsDelete(id);
      toast.success("Deleted");
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      name: row.name || "",
      report_date: toYMD(row.report_date),
      hba1c: row.hba1c != null ? String(row.hba1c) : "",
    });
  };

  const handleEditSave = async () => {
    if (!editing?.id) return;
    setEditSaving(true);
    try {
      await companyAdminCustomersAPI.labReportsUpdate(editing.id, {
        name: editForm.name.trim() || "Lab report",
        report_date: editForm.report_date || undefined,
        hba1c: editForm.hba1c === "" ? null : Number(editForm.hba1c),
      });
      toast.success("Report updated");
      setEditing(null);
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.message || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lab-reports-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Lab reports</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Uploaded files and HbA1c trend for this customer.
          </p>
        </div>
        {canEdit && (
          <Button
            className="gap-2"
            onClick={() => {
              setForm(emptyUploadForm());
              setUploadOpen(true);
            }}
            data-testid="lab-report-upload-open"
          >
            <Upload className="w-4 h-4" />
            Upload report
          </Button>
        )}
      </div>

      {/* HbA1c trend */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="text-sm font-semibold text-foreground mb-1">
          Lab results trend — HbA1c
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Improvement over time when HbA1c % is logged on a report.
        </p>
        {chartData.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-muted-foreground bg-muted rounded-xl">
            No HbA1c values yet. Add % when uploading or editing a report.
          </div>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8edf5" />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  domain={["auto", "auto"]}
                  unit="%"
                  width={42}
                />
                <Tooltip
                  formatter={(v) => [`${v}%`, "HbA1c"]}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.dateShort || ""
                  }
                />
                <Line
                  type="monotone"
                  dataKey="hba1c"
                  stroke="#3B82F6"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: "#3B82F6" }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {chartData.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2">
            {chartData
              .map((p) => `${p.dateLabel} ${p.hba1c}%`)
              .join(" → ")}
          </p>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Report name</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>HbA1c</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-10">
                  No lab reports yet.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((row) => (
                <TableRow key={row.id} data-testid={`lab-report-row-${row.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-sm text-foreground">
                          {row.name}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {row.source === "mobile" ? "From app" : "Admin upload"}
                          {row.original_name ? ` · ${row.original_name}` : ""}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDisplayDate(row.report_date)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.hba1c != null ? `${row.hba1c}%` : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1 h-8"
                        asChild
                      >
                        <a
                          href={fileHref(row.file_url)}
                          target="_blank"
                          rel="noreferrer"
                          download
                          data-testid={`lab-report-download-${row.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </a>
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => openEdit(row)}
                            title="Edit name / date / HbA1c"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(row)}
                            data-testid={`lab-report-delete-${row.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Upload modal */}
      {uploadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => !saving && setUploadOpen(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4 app-modal-shell overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="lab-report-upload-modal"
          >
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-foreground">Upload report</h4>
              <button
                type="button"
                onClick={() => !saving && setUploadOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <Label>Report name</Label>
              <Input
                className="mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. HbA1c Jun 2026"
                data-testid="lab-report-name"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={form.report_date}
                onChange={(e) =>
                  setForm({ ...form, report_date: e.target.value })
                }
                data-testid="lab-report-date"
              />
            </div>
            <div>
              <Label>HbA1c % (optional)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="30"
                className="mt-1"
                value={form.hba1c}
                onChange={(e) => setForm({ ...form, hba1c: e.target.value })}
                placeholder="e.g. 5.8"
                data-testid="lab-report-hba1c"
              />
            </div>
            <div>
              <Label>File</Label>
              <Input
                type="file"
                className="mt-1"
                accept="image/*,.pdf,.doc,.docx"
                onChange={(e) =>
                  setForm({ ...form, file: e.target.files?.[0] || null })
                }
                data-testid="lab-report-file"
              />
            </div>
            <Button
              className="w-full gap-2"
              disabled={saving || !form.file}
              onClick={handleUpload}
              data-testid="lab-report-upload-submit"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {saving ? "Uploading…" : "Upload"}
            </Button>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => !editSaving && setEditing(null)}
        >
          <div
            className="bg-card rounded-2xl shadow-xl w-full max-w-md p-5 space-y-4 app-modal-shell overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-foreground">Edit report</h4>
              <button
                type="button"
                onClick={() => !editSaving && setEditing(null)}
                className="p-1 rounded-lg hover:bg-slate-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <Label>Report name</Label>
              <Input
                className="mt-1"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={editForm.report_date}
                onChange={(e) =>
                  setEditForm({ ...editForm, report_date: e.target.value })
                }
              />
            </div>
            <div>
              <Label>HbA1c %</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="30"
                className="mt-1"
                value={editForm.hba1c}
                onChange={(e) =>
                  setEditForm({ ...editForm, hba1c: e.target.value })
                }
                placeholder="Leave empty to clear"
              />
            </div>
            <Button
              className="w-full"
              disabled={editSaving}
              onClick={handleEditSave}
            >
              {editSaving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
